import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import authService from '../services/authService';
import { API_BASE_URL, OIDC_REDIRECT_URI, OIDC_POST_LOGOUT_REDIRECT_URI } from '../config';
import { logger } from '../utils/logger';
import LocalStorageService from '../utils/localStorageService';
import { readSystemInitialized } from './systemInitialization';

// Define roles and their hierarchy
export const ROLES = {
  USER: 'user',
  MANAGER: 'manager',
  ADMIN: 'admin'
};

// Role hierarchy (higher index has more permissions)
export const ROLE_HIERARCHY = [
  ROLES.USER,
  ROLES.MANAGER,
  ROLES.ADMIN
];

// Define permissions for each feature
export const PERMISSIONS = {
  VIEW_OWN_CV: 'view_own_cv',
  EDIT_OWN_CV: 'edit_own_cv',
  VIEW_OWN_JOB_SKILLS: 'view_own_job_skills',
  EDIT_OWN_JOB_SKILLS: 'edit_own_job_skills',
  VIEW_SUBORDINATE_CV: 'view_subordinate_cv',
  EDIT_SUBORDINATE_CV: 'edit_subordinate_cv',
  VIEW_SUBORDINATE_JOB_SKILLS: 'view_subordinate_job_skills',
  EDIT_SUBORDINATE_JOB_SKILLS: 'edit_subordinate_job_skills',
  VIEW_SUBORDINATE_TRAINING: 'view_subordinate_training',
  EDIT_SUBORDINATE_TRAINING: 'edit_subordinate_training',
  VIEW_ALL_USER_DATA: 'view_all_user_data',
  VIEW_COMPANY_RESEARCH: 'view_company_research',
  EDIT_COMPANY_RESEARCH: 'edit_company_research'
};

// Define which roles have which permissions
export const ROLE_PERMISSIONS = {
  [ROLES.USER]: [
    PERMISSIONS.VIEW_OWN_CV,
    PERMISSIONS.EDIT_OWN_CV,
    PERMISSIONS.VIEW_OWN_JOB_SKILLS,
    PERMISSIONS.EDIT_OWN_JOB_SKILLS
  ],
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_OWN_CV,
    PERMISSIONS.EDIT_OWN_CV,
    PERMISSIONS.VIEW_OWN_JOB_SKILLS,
    PERMISSIONS.EDIT_OWN_JOB_SKILLS,
    PERMISSIONS.VIEW_SUBORDINATE_CV,
    PERMISSIONS.EDIT_SUBORDINATE_CV,
    PERMISSIONS.VIEW_SUBORDINATE_JOB_SKILLS,
    PERMISSIONS.EDIT_SUBORDINATE_JOB_SKILLS,
    PERMISSIONS.VIEW_SUBORDINATE_TRAINING,
    PERMISSIONS.EDIT_SUBORDINATE_TRAINING
  ],
  [ROLES.ADMIN]: [
    PERMISSIONS.VIEW_OWN_CV,
    PERMISSIONS.EDIT_OWN_CV,
    PERMISSIONS.VIEW_OWN_JOB_SKILLS,
    PERMISSIONS.EDIT_OWN_JOB_SKILLS,
    PERMISSIONS.VIEW_SUBORDINATE_CV,
    PERMISSIONS.VIEW_SUBORDINATE_JOB_SKILLS,
    PERMISSIONS.VIEW_SUBORDINATE_TRAINING,
    PERMISSIONS.VIEW_ALL_USER_DATA,
    PERMISSIONS.VIEW_COMPANY_RESEARCH,
    PERMISSIONS.EDIT_COMPANY_RESEARCH
  ]
};

const AuthContext = createContext();
const SYSTEM_INIT_TIMEOUT_MS = 3000;
const SESSION_VALIDATE_TIMEOUT_MS = 2500;
const AUTH_BOOTSTRAP_TIMEOUT_MS = 4000;
let authInitializationPromise = null;

// Admin user object
const ADMIN_USER = {
  id: 'admin',
  email: 'admin@example.com',
  name: 'Administrator',
  role: ROLES.ADMIN,
  isAdmin: true
};

// Admin localStorage key (temporary solution)
const ADMIN_SESSION_KEY = 'autobot_admin_session';
const SESSION_TOKEN_KEY = 'autobot_session_token';

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearLocalSessionState = useCallback(() => {
    setCurrentUser(null);
    setUserRole(null);
    authService.setCurrentUser(null);
    try {
      LocalStorageService.removeItem(ADMIN_SESSION_KEY);
      LocalStorageService.removeItem(SESSION_TOKEN_KEY);
      LocalStorageService.removeItem('currentUser');
      logger.info('[AUTH_CONTEXT]', 'All user session data cleared from localStorage');
    } catch (e) {
      logger.error('[AUTH_CONTEXT]', 'Error removing localStorage item:', e);
    }
  }, []);

  const applyUserSession = useCallback((user) => {
    const role = user?.role || ROLES.USER;
    authService.setCurrentUser(user);
    setCurrentUser(user);
    setUserRole(role);
    return { user, role };
  }, []);

  // Initialize system if needed - extracted from main auth flow
  const initializeSystem = useCallback(async () => {
    try {
      // Direct API call for system initialization status
      const response = await axios.get(`${API_BASE_URL}/system/status`, {
        timeout: SYSTEM_INIT_TIMEOUT_MS
      });
      const isInitialized = readSystemInitialized(response?.data);

      if (!isInitialized) {
        logger.info('[AUTH_CONTEXT]', 'System not initialized, initializing...');
        // Initialize system via API call
        await axios.post(`${API_BASE_URL}/system/initialize`, null, {
          timeout: SYSTEM_INIT_TIMEOUT_MS
        });
      }
    } catch (error) {
      logger.warn('[AUTH_CONTEXT]', 'System initialization check failed fast; continuing auth bootstrap', {
        message: error?.message
      });
    }
  }, []);

  // Restore user session from various sources
  const restoreUserSession = useCallback(async () => {
    // Check for admin session in localStorage
    const adminSessionRaw = LocalStorageService.getItem(ADMIN_SESSION_KEY);
    if (adminSessionRaw) {
      try {
        const adminSession = typeof adminSessionRaw === 'string' ? JSON.parse(adminSessionRaw) : adminSessionRaw;
        if (adminSession && adminSession.id === ADMIN_USER.id) {
          logger.info('[AUTH_CONTEXT]', 'Admin session found in localStorage');
          return applyUserSession(adminSession);
        }
      } catch (e) {
        logger.error('Error checking localStorage:', e);
      }
    }

    const storedUserData = LocalStorageService.getItem('currentUser');
    let cachedUser = null;
    if (storedUserData) {
      try {
        const user = typeof storedUserData === 'string' ? JSON.parse(storedUserData) : storedUserData;
        if (user && user.id && user.email) {
          logger.info('[AUTH_CONTEXT]', 'Restored cached user from localStorage before token validation');
          cachedUser = user;
          applyUserSession(user);
        }
      } catch (error) {
        logger.error('[AUTH_CONTEXT]', 'Error parsing stored user data:', error);
        LocalStorageService.removeItem('currentUser');
      }
    }

    // Check for a valid session token
    const sessionToken = LocalStorageService.getItem(SESSION_TOKEN_KEY);
    if (sessionToken) {
      try {
        logger.info('[AUTH_CONTEXT]', 'Session token found, validating with server...');
        // Validate session with backend
        const response = await axios.post(`${API_BASE_URL}/auth/validate-token`, {
          token: sessionToken
        }, {
          timeout: SESSION_VALIDATE_TIMEOUT_MS
        });

        if (response.data.valid) {
          const user = response.data.user;
          logger.info('[AUTH_CONTEXT]', 'Session token validated, restoring session for:', user.id);
          // Store user data in localStorage for persistence
          LocalStorageService.setItem('currentUser', user);
          return applyUserSession(user);
        }

        logger.info('[AUTH_CONTEXT]', 'Invalid session token, cleared from localStorage');
        LocalStorageService.removeItem(SESSION_TOKEN_KEY);
        if (!cachedUser) {
          clearLocalSessionState();
        }
      } catch (error) {
        logger.warn('[AUTH_CONTEXT]', 'Session token validation failed fast; clearing token', {
          message: error?.message
        });
        LocalStorageService.removeItem(SESSION_TOKEN_KEY);
        if (!cachedUser) {
          clearLocalSessionState();
        }
      }
    }

    if (cachedUser) {
      return { user: cachedUser, role: cachedUser.role || ROLES.USER };
    }

    // If no valid session token, user is not authenticated
    logger.info('[AUTH_CONTEXT]', 'No valid session found, user needs to login');

    return null;
  }, [applyUserSession, clearLocalSessionState]);

  // Initialize auth context - optimized with separated concerns
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        if (!authInitializationPromise) {
          authInitializationPromise = Promise.allSettled([
            initializeSystem(),
            restoreUserSession()
          ]);
        }

        await Promise.race([
          authInitializationPromise,
          new Promise((resolve) => {
            setTimeout(resolve, AUTH_BOOTSTRAP_TIMEOUT_MS);
          })
        ]);
      } catch (error) {
        logger.error('Error initializing auth context:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [initializeSystem, restoreUserSession]);

  async function startOidcLogin() {
    const response = await axios.get(`${API_BASE_URL}/auth/oidc/login`, {
      params: { redirect_uri: OIDC_REDIRECT_URI }
    });
    const authUrl = response?.data?.authorization_url;
    if (!authUrl) throw new Error('Missing authorization_url');
    window.location.assign(authUrl);
  }

  async function completeOidcCallback(code, state) {
    const response = await axios.get(`${API_BASE_URL}/auth/oidc/callback`, {
      params: { code, state, redirect_uri: OIDC_REDIRECT_URI }
    });

    const token = response?.data?.token;
    const user = response?.data?.user;
    if (!token || !user) throw new Error('Invalid OIDC callback response');

    LocalStorageService.setItem(SESSION_TOKEN_KEY, token);
    LocalStorageService.setItem('currentUser', user);
    authService.setCurrentUser(user);

    setCurrentUser(user);
    setUserRole(user.role || ROLES.USER);

    return { success: true, user };
  }

  async function startOidcLogoutFlow() {
    const token = LocalStorageService.getItem(SESSION_TOKEN_KEY);
    const params = new URLSearchParams({
      post_logout_redirect_uri: OIDC_POST_LOGOUT_REDIRECT_URI
    });
    if (token) params.set('token', token);

    clearLocalSessionState();

    try {
      // Best-effort clear of backend session state for local-only session mode.
      await axios.delete(`${API_BASE_URL}/auth/session`);
    } catch (error) {
      logger.warn('[AUTH_CONTEXT]', 'Failed to clear local session before OIDC logout redirect:', error);
    }

    try {
      // Prefer XHR-based local logout URL retrieval then redirect to Keycloak directly.
      const response = await axios.get(`${API_BASE_URL}/auth/oidc/logout-url`, {
        params: {
          post_logout_redirect_uri: OIDC_POST_LOGOUT_REDIRECT_URI,
          token: token || undefined
        }
      });
      const endSessionUrl = response?.data?.end_session_url;
      if (!endSessionUrl) throw new Error('Missing end_session_url');
      window.location.assign(endSessionUrl);
      return true;
    } catch (error) {
      logger.error('[AUTH_CONTEXT]', 'Failed to start OIDC logout redirect via logout-url endpoint:', error);
      throw error;
    }
  }

  // Logout function
  async function logout() {
    logger.info('[AUTH_CONTEXT]', 'Logging out user via OIDC end-session flow');
    return startOidcLogoutFlow();
  }

  async function oidcLogoutRedirect() {
    return startOidcLogoutFlow();
  }

  // Get all users (for admin)
  async function getAllUsers() {
    try {
      logger.info('[AUTH_CONTEXT]', 'Getting all users');
      return await authService.listUsers();
    } catch (error) {
      logger.error('[AUTH_CONTEXT]', 'Error getting users:', error);

      return [];
    }
  }

  // Update user role (admin only)
  async function updateUserRole(userId, newRole) {
    try {
      logger.info('[AUTH_CONTEXT]', `Updating user role: ${userId} to ${newRole}`);

      // Call the API through authService
      await authService.updateUserRole(userId, newRole);

      // If this is the current user, update current user state
      if (currentUser && currentUser.id === userId) {
        const updatedUser = { ...currentUser, role: newRole };
        setCurrentUser(updatedUser);
        setUserRole(newRole);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error updating user role:', error);
      throw error;
    }
  }

  // Assign a manager to a user
  async function assignManager(userId, managerId) {
    try {
      logger.info('[AUTH_CONTEXT]', `Assigning manager: user=${userId}, manager=${managerId || 'none'}`);

      // Call the API through authService
      await authService.assignManager(userId, managerId);

      // If this is the current user, update current user state
      if (currentUser && currentUser.id === userId) {
        const updatedUser = { ...currentUser, managerId };
        setCurrentUser(updatedUser);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error assigning manager:', error);
      throw error;
    }
  }

  // Update user (name, email, role, department)
  async function updateUser(userId, name, email, role, department = null) {
    try {
      logger.info('[AUTH_CONTEXT]', `Updating user: ${userId}`, { name, email, role, department });

      // Call the API through authService
      await authService.updateUser(userId, name, email, role, department);

      return { success: true };
    } catch (error) {
      logger.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete a user
  async function deleteUser(userId) {
    try {
      logger.info('[AUTH_CONTEXT]', `Deleting user: ${userId}`);

      // Call the API through authService
      await authService.deleteUser(userId);

      return { success: true };
    } catch (error) {
      logger.error('Error deleting user:', error);
      throw error;
    }
  }

  // Get a user's manager
  async function getUserManager(userId) {
    const users = await getAllUsers();
    const user = users.find(u => u.id === userId);

    if (!user || !user.managerId) return null;

    return users.find(u => u.id === user.managerId);
  }

  // Get a user's subordinates
  async function getUserSubordinates(userId) {
    try {
      const users = await getAllUsers();

      // Make sure we have an array of users and filter out subordinates
      const result = Array.isArray(users)
        ? users.filter(u => u.manager_id === userId)
        : [];

      logger.info('[AUTH_CONTEXT]', `Found ${result.length} subordinates for user ${userId}`);
      return result;
    } catch (error) {
      logger.error('[AUTH_CONTEXT]', 'Error getting subordinates:', error);

      // Return empty array on error
      return [];
    }
  }

  // Check if user has a specific permission
  function hasPermission(permission) {
    if (!currentUser || !userRole) return false;

    return ROLE_PERMISSIONS[userRole].includes(permission);
  }

  // Check if user has sufficient role level
  function hasRole(requiredRole) {
    if (!currentUser || !userRole) return false;

    const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole);
    const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);

    return userRoleIndex >= requiredRoleIndex;
  }

  // Check if a user can access another user's data
  async function canAccessUserData(targetUserId, _forEdit = false) {
    // Admins can access all users' data
    if (userRole === ROLES.ADMIN) {
      return true;
    }

    // Users can access their own data
    if (currentUser && currentUser.id === targetUserId) {
      return true;
    }

    // Managers can access their subordinates' data
    if (userRole === ROLES.MANAGER) {
      const subordinates = await getUserSubordinates(currentUser.id);
      const subordinateIds = subordinates.map(sub => sub.id);
      return subordinateIds.includes(targetUserId);
    }

    return false;
  }

  // Get the author type based on user role and target user
  function getAuthorType(targetUserId, isAIGenerated = false) {
    if (isAIGenerated) {
      return 'AI';
    }

    if (currentUser && currentUser.id === targetUserId) {
      return 'USER';
    }

    return userRole ? userRole.toUpperCase() : 'UNKNOWN';
  }

  // Save user CV data with version history
  async function saveUserCV(userId, cvData) {
    try {
      // Ensure we can access this user's data
      if (!await canAccessUserData(userId, true)) {
        throw new Error('You do not have permission to save this user\'s CV');
      }

      // Add timestamp if not present
      const dataToSave = {
        ...cvData,
        lastUpdated: new Date().toISOString()
      };

      logger.info('[AUTH_CONTEXT]', 'saveUserCV - Not implemented, data prepared:', dataToSave);

      return dataToSave;
    } catch (error) {
      logger.error('Error saving user CV:', error);
      throw error;
    }
  }

  // Get user CV data
  async function getUserCV(userId) {
    if (!await canAccessUserData(userId)) {
      throw new Error('You do not have permission to view this user\'s CV');
    }

    try {
      // Load directly from cv_analyzer via API
      const response = await axios.get(`${API_BASE_URL}/training/user/cv/${userId}`);
      if (response.data && response.data.result) {
        logger.info('[AUTH_CONTEXT]', `Loaded CV data for user ${userId} from API`);
        return response.data.result;
      }
      return null;
    } catch (error) {
      logger.error('Error getting user CV:', error);
      return null;
    }
  }

  // Get user CV version history
  async function getUserCVVersions(_userId) {
    return {};
  }

  // Get active CV version
  function getActiveCVVersion(_userId) {
    return null;
  }

  // Save user job skills data with version history
  async function saveUserJobSkills(userId, skillsData) {
    try {
      // Ensure we can access this user's data
      if (!await canAccessUserData(userId, true)) {
        throw new Error('You do not have permission to save this user\'s job skills');
      }

      // Add timestamp if not present
      const dataToSave = {
        ...skillsData,
        lastUpdated: new Date().toISOString()
      };

      return dataToSave;
    } catch (error) {
      logger.error('Error saving user job skills:', error);

      throw error;
    }
  }

  // Get user job skills data
  async function getUserJobSkills(userId) {
    if (!await canAccessUserData(userId)) {
      throw new Error('You do not have permission to view this user\'s job skills');
    }

    try {
      // Load directly from job_analyzer via API
      const response = await axios.get(`${API_BASE_URL}/training/user/job-skills/${userId}`);
      if (response.data && response.data.result) {
        logger.info('[AUTH_CONTEXT]', `Loaded job skills data for user ${userId} from API`);
        return response.data.result;
      }
      return null;
    } catch (error) {
      logger.error('Error getting user job skills:', error);
      return null;
    }
  }

  // Get user job skills version history
  async function getUserJobSkillsVersions(_userId) {
    return {};
  }

  // Get active job skills version
  function getActiveJobSkillsVersion(_userId) {
    return null;
  }

  // Save company research data (admin only)
  async function saveCompanyResearch(_researchData) {
    if (userRole !== ROLES.ADMIN) {
      throw new Error('Only admins can save company research');
    }

    logger.info('[AUTH_CONTEXT]', 'saveCompanyResearch - Not implemented');
  }

  // Get company research data (admin only)
  async function getCompanyResearch() {
    if (userRole !== ROLES.ADMIN) {
      throw new Error('Only admins can view company research');
    }

    try {
      // Load directly from company_research folder via API
      const response = await axios.get(`${API_BASE_URL}/company/research`);
      if (response.data && response.data.result) {
        logger.info('[AUTH_CONTEXT]', 'Loaded company research data from API');
        return response.data.result;
      }
      return null;
    } catch (error) {
      logger.error('Error getting company research:', error);
      return null;
    }
  }

  // Get all accessible users based on current user's role and position
  async function getAccessibleUsers() {
    const allUsers = await getAllUsers();

    if (userRole === ROLES.ADMIN) {
      // Admins can see all users
      return allUsers;
    }
    if (userRole === ROLES.MANAGER) {
      // Managers can see themselves and their subordinates
      const subordinates = await getUserSubordinates(currentUser.id);
      const subordinateIds = subordinates.map(sub => sub.id);
      return allUsers.filter(user => user.id === currentUser.id || subordinateIds.includes(user.id));
    }

    // Regular users can only see themselves
    return allUsers.filter(user => user.id === currentUser.id);
  }

  // Get information about a specific user by ID
  async function getUserInfo(userId) {
    try {
      if (!await canAccessUserData(userId)) {
        throw new Error('You do not have permission to access this user\'s information');
      }

      // If it's the current user, return their data
      if (currentUser && currentUser.id === userId) {
        return currentUser;
      }

      // Otherwise, get user from database
      // Note: user lookup from backend isn't implemented yet.
      throw new Error('User lookup not implemented');
    } catch (error) {
      logger.error('Error fetching user info:', error);
      throw error;
    }
  }

  const contextValue = {
    currentUser,
    userRole,
    loading,
    startOidcLogin,
    completeOidcCallback,
    logout,
    oidcLogoutRedirect,
    hasPermission,
    hasRole,
    ROLES,
    PERMISSIONS,
    // Admin functions
    getAllUsers,
    updateUserRole,
    updateUser,
    deleteUser,
    // User hierarchy functions
    assignManager,
    getUserManager,
    getUserSubordinates,
    // User data access functions
    canAccessUserData,
    getAccessibleUsers,
    // CV and job skills functions
    saveUserCV,
    getUserCV,
    getUserCVVersions,
    getActiveCVVersion,
    saveUserJobSkills,
    getUserJobSkills,
    getUserJobSkillsVersions,
    getActiveJobSkillsVersion,
    getAuthorType,
    // Company research functions
    saveCompanyResearch,
    getCompanyResearch,
    getUserInfo
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
